/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'
import { useFilters, useUpdateURLSearchParams } from 'hooks/useQuery'
import * as React from 'react'
import tw from 'tailwind-styled-components'

type Props = {
  columns: Array<string | React.ReactNode>
  orderSet?: Array<{
    column: string
    key: string
    defaultOrdering?: 'asc' | 'desc'
  }>
  onColumnClick?: (column: string | React.ReactNode) => void
  compact?: boolean
}

export const TableHeader = tw.th<{ $compact?: boolean }>`
  bg-gray-50
  border-b
  border-gray-200
  font-medium
  leading-4
  pl-6
  pr-3
  py-3
  text-gray-500
  text-left
  text-xs
  tracking-wider
  uppercase

   ${({ $compact }: { $compact?: boolean }) => {
     if ($compact) {
       return `
        py-2
        pl-2
        pr-1
      `
     }

     return ''
   }}
`

function TableHead({ columns, orderSet, onColumnClick, compact }: Props) {
  const { filters } = useFilters()
  const updateParams = useUpdateURLSearchParams()
  const columnsClickStyle =
    'hover:bg-gray-50 border-b border-gray-200 hover:no-underline cursor-pointer'

  const currentOrdering: string[] = React.useMemo(() => {
    const orderings = filters.ordering?.split(',')
    if (!orderings) return []
    return orderings
  }, [filters.ordering])

  const getColumnOrdering = React.useCallback(
    (column: string | React.ReactNode) => {
      if (typeof column !== 'string') return

      const defaultOrdering = orderSet?.find(
        (ordering) => ordering.column === column
      )

      if (!defaultOrdering) {
        return
      }

      const ordering = currentOrdering?.find((ordering) =>
        ordering.includes(defaultOrdering.key)
      )
      if (!ordering) {
        return defaultOrdering.defaultOrdering || 'asc'
      }
      return `-${defaultOrdering.key}` === ordering ? 'desc' : 'asc'
    },
    [currentOrdering, orderSet]
  )

  const handleChangeOrdering = React.useCallback(
    (column: string | React.ReactNode) => {
      if (typeof column !== 'string') return

      const currentColumnOrdering = getColumnOrdering(column)
      const columnKey = orderSet?.find(
        (ordering) => ordering.column === column
      )?.key

      if (!currentColumnOrdering || !columnKey) {
        return
      }

      const newOrderings = [...currentOrdering]

      if (newOrderings.includes(columnKey)) {
        newOrderings.splice(newOrderings.indexOf(columnKey), 1)
      }

      if (newOrderings.includes(`-${columnKey}`)) {
        newOrderings.splice(newOrderings.indexOf(`-${columnKey}`), 1)
      }

      if (currentColumnOrdering === 'asc') {
        newOrderings.push(`-${columnKey}`)
      } else if (currentColumnOrdering === 'desc') {
        newOrderings.push(columnKey)
      }

      // $FlowFixMe
      updateParams(['ordering', newOrderings.join(',')])
    },
    [currentOrdering, getColumnOrdering, orderSet, updateParams]
  )

  return (
    <thead>
      <tr>
        {columns.map((column, index) => (
          <TableHeader
            $compact={compact}
            className={
              onColumnClick || !!getColumnOrdering(column)
                ? columnsClickStyle
                : ''
            }
            key={index}
            onClick={() => {
              if (orderSet) {
                handleChangeOrdering(column)
              }
              if (onColumnClick) {
                onColumnClick(column)
              }
            }}
          >
            {/* @ts-ignore */}
            {column}
            {orderSet &&
              orderSet?.find((ordering) => ordering.column === column) && (
                <>
                  {getColumnOrdering(column) === 'asc' && (
                    <CaretUpOutlined className="ml-1" />
                  )}
                  {getColumnOrdering(column) === 'desc' && (
                    <CaretDownOutlined className="ml-1" />
                  )}
                </>
              )}
          </TableHeader>
        ))}
      </tr>
    </thead>
  )
}

export default TableHead
